/**
 * Performance Predictions Component
 * AI-powered performance forecasting and predictions
 * Phase 6.5 - AI Management Tools
 */

import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Avatar,
  LinearProgress,
  Skeleton,
  Divider
} from '@mui/material';
import {
  Psychology as AIIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  ShowChart as ForecastIcon,
  AttachMoney as RevenueIcon,
  People as TeamIcon,
  EmojiEvents as GoalIcon,
  Warning as RiskIcon
} from '@mui/icons-material';

export interface Prediction {
  metric: string;
  current: number;
  predicted: number;
  confidence: number;
  trend: 'up' | 'down' | 'stable';
  changePercentage: number;
  unit?: string;
  icon?: React.ReactNode;
  color?: 'primary' | 'success' | 'warning' | 'error' | 'info';
}

export interface TeamGoal {
  id: string;
  name: string;
  target: number;
  current: number;
  predicted: number;
  deadline: string;
  status: 'on_track' | 'at_risk' | 'behind';
  aiRecommendation: string;
}

export interface PerformancePredictionsProps {
  predictions: Prediction[];
  goals: TeamGoal[];
  loading?: boolean;
  timeframe?: string;
}

const PerformancePredictions: React.FC<PerformancePredictionsProps> = ({
  predictions,
  goals,
  loading = false,
  timeframe = 'Next Month'
}) => {
  // Get trend icon and color
  const getTrendDisplay = (trend: 'up' | 'down' | 'stable', change: number) => {
    switch (trend) {
      case 'up':
        return {
          icon: <TrendingUpIcon sx={{ fontSize: 18 }} />,
          color: 'success.main',
          text: `+${change}%`
        };
      case 'down':
        return {
          icon: <TrendingDownIcon sx={{ fontSize: 18 }} />,
          color: 'error.main',
          text: `-${Math.abs(change)}%`
        };
      default:
        return {
          icon: null,
          color: 'text.secondary',
          text: '0%'
        };
    }
  };

  // Get goal status color
  const getGoalStatusColor = (status: TeamGoal['status']) => {
    switch (status) {
      case 'on_track':
        return 'success';
      case 'at_risk':
        return 'warning';
      case 'behind':
        return 'error';
      default:
        return 'default';
    }
  };

  // Get confidence color
  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 80) return 'success';
    if (confidence >= 60) return 'info';
    if (confidence >= 40) return 'warning';
    return 'error';
  };

  if (loading) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <ForecastIcon color="primary" />
          <Typography variant="h6" fontWeight="600">
            Performance Predictions
          </Typography>
        </Box>
        <Grid container spacing={2}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} key={i}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ bgcolor: 'warning.main', width: 36, height: 36 }}>
            <ForecastIcon sx={{ fontSize: 20 }} />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight="600">
              Performance Predictions
            </Typography>
            <Typography variant="caption" color="text.secondary">
              AI forecasts for {timeframe}
            </Typography>
          </Box>
        </Box>
        <Chip
          icon={<AIIcon />}
          label="AI Powered"
          size="small"
          color="primary"
          variant="outlined"
        />
      </Box>

      {/* Predictions Grid */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {predictions.map((pred, index) => {
          const trendDisplay = getTrendDisplay(pred.trend, pred.changePercentage);

          return (
            <Grid item xs={12} sm={6} md={6} key={index}>
              <Paper
                sx={{
                  p: 2,
                  height: '100%',
                  border: 1,
                  borderColor: 'divider',
                  '&:hover': { boxShadow: 3 }
                }}
              >
                {/* Metric Name */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  {pred.icon && (
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: `${pred.color || 'primary'}.light`,
                        color: `${pred.color || 'primary'}.main`
                      }}
                    >
                      {pred.icon}
                    </Avatar>
                  )}
                  <Typography variant="subtitle2" fontWeight="600">
                    {pred.metric}
                  </Typography>
                </Box>

                {/* Current vs Predicted */}
                <Box sx={{ mb: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Current
                    </Typography>
                    <Typography variant="body2" fontWeight="500">
                      {pred.current}{pred.unit}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Predicted
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" fontWeight="600" color={trendDisplay.color}>
                        {pred.predicted}{pred.unit}
                      </Typography>
                      {trendDisplay.icon && (
                        <Box sx={{ display: 'flex', alignItems: 'center', color: trendDisplay.color }}>
                          {trendDisplay.icon}
                          <Typography variant="caption" fontWeight="600">
                            {trendDisplay.text}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* Confidence */}
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      AI Confidence
                    </Typography>
                    <Typography variant="caption" fontWeight="600">
                      {pred.confidence}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={pred.confidence}
                    color={getConfidenceColor(pred.confidence)}
                    sx={{ height: 4, borderRadius: 2 }}
                  />
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* Team Goals */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <GoalIcon sx={{ color: 'primary.main', fontSize: 20 }} />
          <Typography variant="subtitle1" fontWeight="600">
            Team Goals Progress
          </Typography>
        </Box>

        {goals.map((goal, index) => {
          const progress = (goal.current / goal.target) * 100;
          const predictedProgress = (goal.predicted / goal.target) * 100;

          return (
            <Paper
              key={goal.id}
              sx={{
                p: 2,
                mb: 2,
                border: 1,
                borderColor: 'divider'
              }}
            >
              {/* Goal Header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Box>
                  <Typography variant="subtitle2" fontWeight="600">
                    {goal.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Deadline: {new Date(goal.deadline).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </Typography>
                </Box>
                <Chip
                  label={goal.status.replace('_', ' ').toUpperCase()}
                  size="small"
                  color={getGoalStatusColor(goal.status)}
                  sx={{ fontWeight: 600 }}
                />
              </Box>

              {/* Progress Bar */}
              <Box sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Current Progress
                  </Typography>
                  <Typography variant="caption" fontWeight="600">
                    {goal.current} / {goal.target}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(progress, 100)}
                  color={getGoalStatusColor(goal.status)}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>

              {/* Predicted Progress */}
              <Box sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Predicted by Deadline
                  </Typography>
                  <Typography variant="caption" fontWeight="600" color="info.main">
                    {goal.predicted} / {goal.target}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(predictedProgress, 100)}
                  color="info"
                  sx={{ height: 6, borderRadius: 3 }}
                />
              </Box>

              {/* AI Recommendation */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  p: 1.5,
                  bgcolor: goal.status === 'on_track' ? 'success.50' : 'warning.50',
                  borderRadius: 1
                }}
              >
                <AIIcon
                  sx={{
                    fontSize: 18,
                    color: goal.status === 'on_track' ? 'success.main' : 'warning.main',
                    mt: 0.25
                  }}
                />
                <Box>
                  <Typography variant="caption" fontWeight="600" sx={{ display: 'block', mb: 0.25 }}>
                    AI Recommendation:
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {goal.aiRecommendation}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          );
        })}
      </Box>

      {/* Empty State */}
      {predictions.length === 0 && goals.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
          <ForecastIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
          <Typography variant="body2">
            No predictions available. Gathering data for AI analysis...
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default PerformancePredictions;
